import * as React from "react";
import { useDispatch } from "react-redux";
import { Container, Divider, Grid, Header, List, Segment } from "semantic-ui-react";
import { debounce } from "lodash";

import { SavingIndicator } from "../../components/SavingIndicator";
import { AnsweredQuestion, AnsweredQuestionWithId, ConsultationDetails, EmployeeRecord, OhCase } from "../../model";
import { AccordionQuestionnaireContainer } from "../../questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "../../questionnaires/components/AccordionQuestionnaireSection";
import { OldDateQuestion } from "../../questionnaires/components/questions/OldDateQuestion";
import { TextQuestion } from "../../questionnaires/components/questions/TextQuestion";
import { TaskModel } from "../../task/taskActions/models";
import {
    getQuestionConfig,
    MHPTreatmentReportModel,
    MHP_TREATMENT_REPORT_KEY,
    outcomeAnswerOptions,
    ReportSectionIds,
    treatmentReceivedAnswerOptions,
    workStatusAtDischargeOptions,
    workStatusAtInitialAssessmentOptions
} from "../models";
import * as questionIds from "../mhpTreatmentReportQuestionIds";
import { consultationMethodOptions, ExtraSection, useHrAndLineManagers } from "../../reportsCommon/reportsCommon";
import { getAnswerToQuestionWithId } from "../../questionnaires/getAnswerToQuestionWithId";
import { MultiCheckbox, MultiCheckboxQuestion } from "../../questionnaires/components/questions/MultiCheckboxQuestion";
import { DropdownQuestion } from "../../questionnaires/components/questions/DropdownQuestion";
import { debounceDelayHigh } from "../../filtering/constants";
import { usePrevious } from "../../customHooks";
import { PrintDialog } from "../../components/PrintDialog";
import { EmployeeReleaseReportWithCommentsInfo } from "../../reportsCommon/components/EmployeeReleaseReportWithCommentsInfo";
import { updateQuestion, UpdateQuestionPayload } from "../../questionnaires/actions";
import { CustomQuestionConfig } from "../../questionnaires/questionnaireConfig";
import { NumberQuestion } from "../../questionnaires/components/questions/NumberQuestion";
import { QuestionnaireTypes } from "../../questionnaires/model";
import { ConsentWithdrawnInfo } from "../../reportsCommon/components/ConsentWithdrawnInfo";

interface Props {
    readonly: boolean;
    customerId: string;
    employeeRecord: EmployeeRecord | null;
    ohCase: OhCase | null;
    task: TaskModel | null;
    report: MHPTreatmentReportModel;
    isFromAdmin: boolean;
    isReportPreview: boolean;
    adminButtons?: React.ReactNode;
    extraQuestionContent?: { [questionId: string]: React.ReactNode };
    loadReport: () => Promise<void>;
    saveReport?: (questions: AnsweredQuestionWithId[], eTag: string) => Promise<void>;
    submitReport?: () => Promise<boolean>;
    consultationAndReferralDetails: ConsultationDetails;
    extraSections?: ExtraSection[];
}

export const MHPTreatmentReport: React.FunctionComponent<Props> = (props: Props) => {

    const dispatch = useDispatch();

    const [saving, setSaving] = React.useState<boolean>(false);

    const previousReport = usePrevious(props.report);

    const showAdminButtons = !props.isReportPreview && props.isFromAdmin && props.ohCase;

    const saveDebounced = React.useCallback(
        debounce(async (updatedQuestions: AnsweredQuestionWithId[], eTag: string) => {
            try {
                if (props.readonly === false) {
                    await props.saveReport(updatedQuestions, eTag);
                }
            } catch (ex) {
                if (ex.status === 400) {
                    props.loadReport();
                } else {
                    throw ex;
                }
            }
            finally {
                setSaving(false);
            }
        }, debounceDelayHigh),
        [props.readonly]
    );

    React.useEffect(() => {
        (async () => {
            if (props.readonly === false) {
                const answersUpdated = props.report.questions?.some((q, i) => q !== previousReport?.questions[i]);
                if (answersUpdated) {
                    setSaving(true);
                    await saveDebounced(props.report.questions, props.report.eTag);
                }
            }
        })();
    }, [props.readonly, props.report, previousReport]);

    const updateAnswer = (sectionId: string, questionId: string, answeredQuestion: AnsweredQuestion) => {
        const payload: UpdateQuestionPayload = {
            questionnaireKey: MHP_TREATMENT_REPORT_KEY,
            sectionId,
            id: questionId,
            question: {
                ...answeredQuestion,
                isValid: true
            }
        };
        dispatch(updateQuestion(payload));
    };

    const hrAndLineManagers = useHrAndLineManagers(props.consultationAndReferralDetails);

    const consultationMethodAnswer = getAnswerToQuestionWithId(props.report.questions, questionIds.IntroductionAndDataPrivacyConsultationMethod);

    React.useEffect(() => {
        const consultationMethodText = consultationMethodOptions.find(o => o.value === consultationMethodAnswer)?.text;
        const answer = `${props.consultationAndReferralDetails?.consultationDate?.format("DD/MM/YYYY")}, ${consultationMethodText}`;
        const dateMethodQuestionConfig = getQuestionConfig(questionIds.DateMethod) as CustomQuestionConfig;
        const answeredQuestion: AnsweredQuestion = {
            answer,
            question: dateMethodQuestionConfig.questionText,
            isValid: true
        };
        updateAnswer(ReportSectionIds.EmployeeDetails, questionIds.DateMethod, answeredQuestion);
    }, [consultationMethodAnswer]);

    return (
        <Container className="referral-report">
            {showAdminButtons ?
                <Container textAlign="right" className="start-button">{props.adminButtons}</Container> :
                <Grid container>
                    <Grid.Row className="report-title-row">
                        <Grid.Column>
                            <Header as="h1">Mental Health Practitioner Discharge Form</Header>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            }
            <SavingIndicator display={saving} />
            <AccordionQuestionnaireContainer
                loadQuestionnaire={props.loadReport}
                submitQuestionnaire={props.submitReport}
                readonly={props.readonly}
                questionnaireKey={MHP_TREATMENT_REPORT_KEY}
                extraQuestionContent={props.extraQuestionContent}
                // eslint-disable-next-line max-len
                submissionConfirmationText={`You are submitting this report for ${props.employeeRecord?.firstName} ${props.employeeRecord?.lastName}. Are you happy to continue?`}
                questionnaireType={QuestionnaireTypes.Report}
            >
                <ConsentWithdrawnInfo reportDetails={props.report} />
                {props.employeeRecord &&
                    <>
                        <Header>PRIVATE AND CONFIDENTIAL</Header>
                        <List>
                            <List.Item>{props.employeeRecord.firstName} {props.employeeRecord.lastName}</List.Item>
                            {[
                                props.employeeRecord.addressLine1,
                                props.employeeRecord.addressLine2,
                                props.employeeRecord.addressLine3,
                                props.employeeRecord.city,
                                props.employeeRecord.county,
                                props.employeeRecord.postCode
                            ].filter(a => !!a).map((addressLine, index) => <List.Item key={index}>{addressLine}</List.Item>)}
                        </List>
                    </>
                }
                <AccordionQuestionnaireSection
                    title={props.report.isDefaultCustomerType === false ? "Details" : "Employee Details"}
                    id={ReportSectionIds.EmployeeDetails}
                    hideNextButton
                    active
                >
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={({ id: questionIds.Employee, questionText: props.report.isDefaultCustomerType === false ? "Name" : "Employee" })}
                        answer={props.employeeRecord ? `${props.employeeRecord.firstName} ${props.employeeRecord.lastName}` : ""}
                        readonly
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployeeId)}
                        answer={props.consultationAndReferralDetails?.employeeId}
                        hidden
                        readonly
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployerAndDivision)}
                        answer={props.employeeRecord &&
                            [
                                props.employeeRecord.customerName,
                                props.employeeRecord.orgStructureOne?.name
                            ].filter(t => t).join(" - ")
                        }
                        hidden
                        readonly
                    />
                    <OldDateQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.DateOfBirth)}
                        answer={props.employeeRecord?.dateOfBirth?.format("YYYY-MM-DD")}
                        readonly
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.PersonalReferenceNumber)}
                        answer={props.employeeRecord ? `${props.employeeRecord?.personalReferenceNumber}` : ""}
                        readonly
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployeeUniqueID)}
                        answer={props.employeeRecord ? `${props.employeeRecord?.secondaryReference}` : ""}
                        readonly
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.JobRole)}
                        answer={props.employeeRecord ? `${props.employeeRecord.jobTitle}` : ""}
                        readonly
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.HrManager)}
                        answer={hrAndLineManagers}
                        readonly
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.Worksite)}
                        answer={props.employeeRecord &&
                            [
                                props.employeeRecord.orgStructureOne?.name,
                                props.employeeRecord.orgStructureTwo?.name
                            ].filter(t => t).join(" - ")
                        }
                        readonly
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.DateMethod)}
                        readonly
                    />
                    <Segment basic>
                        Thank you for your referral. I have undertaken a consultation today to discuss this referral, the
                        presenting health situation, fitness for work and identify any workplace support required. Verbal consent to
                        proceed with this consultation and generation of a functional outcome report has been confirmed. The general
                        content of the report has been discussed and a copy of the report will be sent via the portal.
                    </Segment>
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Referral Details"
                    id={ReportSectionIds.ReferralDetails}
                    hideNextButton
                    active
                >
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.ReasonForReferral)}
                        multiline
                    />
                    {/* TODO pull text from the consultation */}
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.Diagnosis)}
                        multiline
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.NumberOfSessions)}
                    />
                    <TextQuestion
                        hidden
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.IntroductionAndDataPrivacyConsultationMethod)}
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Treatment Received"
                    id={ReportSectionIds.TreatmentReceived}
                    hideNextButton
                    active
                >
                    <MultiCheckboxQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.TreatmentReceivedAnswers)}
                        readonly={props.readonly}
                        options={treatmentReceivedAnswerOptions}
                    >
                        {(Object.keys(treatmentReceivedAnswerOptions) as (keyof typeof treatmentReceivedAnswerOptions)[]).map(k =>
                            <MultiCheckbox key={k} value={k} label={treatmentReceivedAnswerOptions[k]} />)}
                    </MultiCheckboxQuestion>
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Outcome"
                    id={ReportSectionIds.Outcome}
                    hideNextButton
                    active
                >
                    <MultiCheckboxQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.OutcomeAnswers)}
                        readonly={props.readonly}
                        options={outcomeAnswerOptions}
                    >
                        {(Object.keys(outcomeAnswerOptions) as (keyof typeof outcomeAnswerOptions)[]).map(k =>
                            <MultiCheckbox key={k} value={k} label={outcomeAnswerOptions[k]} />)}
                    </MultiCheckboxQuestion>
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Work Status"
                    id={ReportSectionIds.WorkStatus}
                    hideNextButton
                    active
                >
                    <DropdownQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.WorkStatusAtInitialAssessment)}
                        options={workStatusAtInitialAssessmentOptions}
                    />
                    <DropdownQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.WorkStatusAtDischarge)}
                        options={workStatusAtDischargeOptions}
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Questionnaire Scores"
                    id={ReportSectionIds.QuestionnaireScores}
                    hideNextButton
                    active
                >
                    <NumberQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.QuestionnaireScoresAtInitialAssessmentPHQ9)}
                    />
                    <NumberQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.QuestionnaireScoresAtInitialAssessmentGAD7)}
                    />
                    <NumberQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.QuestionnaireScoresAtInitialAssessmentWSAS)}
                    />
                    <NumberQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.QuestionnaireScoresAtDischargePHQ9)}
                    />
                    <NumberQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.QuestionnaireScoresAtDischargeGAD7)}
                    />
                    <NumberQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.QuestionnaireScoresAtDischargeWSAS)}
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title="Further Adjustments/Recommendations"
                    id={ReportSectionIds.FurtherAdjustmentsRecommendations}
                    hideNextButton
                    active
                >
                    <TextQuestion
                        hideDivider
                        inline
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.FurtherAdjustmentsRecommendationsComments)}
                        multiline
                    />
                </AccordionQuestionnaireSection>
                {props.extraSections && props.extraSections.map((s, i) =>
                    (<AccordionQuestionnaireSection key={i} title={s.title} id={s.id} hideNextButton={true} active={true} neverHide={true}>
                        {s.content}
                    </AccordionQuestionnaireSection>))
                }
                {props.readonly &&
                    <>
                        <Segment basic>
                            <p>
                                The above information is advisory only. The possibility of implementing any suggestions is purely a decision by the employer,
                                for example, any rehabilitation programme or adjustment to working practice must be agreed by the employer, taking into account
                                the needs of the service and any constraints within the context of the work environment.
                            </p>
                            <p>
                                I hope you find this information useful. Please do not hesitate to contact us with any queries.
                            </p>
                        </Segment>
                        <Segment basic>
                            <p>
                                <strong>{props.consultationAndReferralDetails?.occupationalHealthAdviserName}</strong>
                                <br />
                                <i>{props.consultationAndReferralDetails?.occupationalHealthAdviserTitle}</i>
                            </p>
                            <p>
                                {props.consultationAndReferralDetails?.occupationalHealthAdviserQualification}
                            </p>
                            <p>
                                Please note that any advice given and suggested adjustments contained within this report are purely recommendations and the
                                opinion of the clinician. It is ultimately a management decision as to whether or not these can be
                                accommodated.
                            </p>
                            <p>
                                If you would like to discuss this report or require further information or advice, please do not hesitate to contact
                            us: {props.consultationAndReferralDetails?.customerEmail} or call {props.consultationAndReferralDetails?.customerPhone}.
                            </p>
                        </Segment>
                        <EmployeeReleaseReportWithCommentsInfo reportDetails={props.report} />
                        <Divider />
                        <Container>
                            <p>
                                Printing Disclaimer:  This report contains personal identifiable information and sensitive health details.
                                It is possible to print this report or create a pdf of this report using your in-browser printing however
                                we would strongly recommend that you only view this report in the system and avoid printing where ever possible.
                                If you do choose to print or export this information then you do so at your own risk and you must take all necessary
                                measures to protect the information contained within this report and ensure that it is only viewed by authorised
                                personnel within your own organisation.
                            </p>
                            <PrintDialog />
                        </Container>
                    </>
                }
            </AccordionQuestionnaireContainer>
        </Container>
    );
};
